import { LoadingSpinner } from '@components/Generic'
import { observer } from 'mobx-react'
import { useContext, useEffect, useState } from 'react'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@methods/axiosConfig'
import { mapCountriesFromSaltedgeArray } from '@/methods/mapCountries'
import { forceRedirect } from '@/methods/forceRedirect'
import { devLog } from '@/methods/devLog'
import { SelectCountry } from '@/components/Saltedge/steps/SelectCountry'
import { SelectBank } from '@/components/Saltedge/steps/SelectBank'
import { SaltedgeCountry, SaltedgeProvider } from '@/types/saltedge'
import { Redirect } from '@/components/Saltedge/steps/Redirect'
import { API_URL } from '@constants/apiUrl'

export const SaltedgePage = observer(() => {
  const store = useContext(StoreContext)
  const { isMobile } = store.AppState
  const { currentScenarioId } = store.ScenarioState

  const [step, setStep] = useState<number>(0)
  const [country, setCountry] = useState<SaltedgeCountry | null>(null)
  const [apiCountries, setApiCountries] = useState<SaltedgeCountry[]>([])
  const [providers, setProviders] = useState<SaltedgeProvider[]>([])

  const nextStep = () => {
    setStep(step + 1)
  }

  const prevStep = () => {
    setProviders([])
    setStep(step - 1)
  }

  const getCountries = async () => {
    try {
      const res = await axiosInstance.get(`${API_URL}/salt-edge/countries`, {
        withCredentials: true,
      })

      const countriesArray = mapCountriesFromSaltedgeArray(res.data.countries)

      res.data?.countries && setApiCountries(countriesArray)
      nextStep()
    } catch (e) {
      throw e
    }
  }

  const handleProceed = async () => {
    if (!country) return

    if (currentScenarioId !== 'localTests') {
      nextStep()

      const res = await axiosInstance.get(
        `${API_URL}/salt-edge/providers?country=${country.value}`,
        {
          withCredentials: true,
        }
      )
      res.data?.providers && setProviders(res.data?.providers)
    }
  }

  const handleCountry = (country: SaltedgeCountry) => {
    if (country) {
      const countryApiObj = apiCountries.find((apiObj) => apiObj.countryCode === country.value)

      setCountry(countryApiObj ?? null)
    } else {
      setCountry(null)
    }
  }

  useEffect(() => {
    if (apiCountries.length === 1) {
      handleCountry(apiCountries[0])
    }

    if (currentScenarioId === 'localTests') {
      const countryApiObj = apiCountries.find((apiObj) => apiObj.countryCode === 'PL') ?? null

      setCountry(countryApiObj)
    }
  }, [apiCountries])

  useEffect(() => {
    getCountries()
  }, [])

  const initSaltedge = async (provider: string) => {
    nextStep()

    try {
      const { data } = await axiosInstance.post<{ redirectUrl: string }>(
        `${API_URL}/salt-edge/start-session`,
        { provider: provider },
        {
          withCredentials: true,
        }
      )

      forceRedirect(data.redirectUrl)
    } catch (error) {
      devLog('error: ', error)
    }
  }

  const renderCurrentStep = () => {
    switch (step) {
      case 0:
        return <LoadingSpinner />
      case 1:
        return (
          <SelectCountry
            apiCountries={apiCountries}
            country={country}
            isMobile={isMobile}
            handleCountry={handleCountry}
            handleProceed={handleProceed}
          />
        )
      case 2:
        return (
          <SelectBank
            providers={providers}
            country={country}
            initSaltedge={initSaltedge}
            prevStep={prevStep}
          />
        )
      case 3:
        return <Redirect />
      default:
        return <></>
    }
  }

  return <div className="page-container mobile-m-10">{renderCurrentStep()}</div>
})
